import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import Vue from 'vue';
import PendoAlert from '../../components/alert/pendo-alert.vue.js';
import PendoButton from '../../components/button/pendo-button.vue.js';
import PendoCheckbox from '../../components/checkbox/pendo-checkbox.vue.js';
import PendoCheckboxGroup from '../../components/checkbox-group/pendo-checkbox-group.vue.js';
import PendoInput from '../../components/input/pendo-input.vue.js';
import PendoLegalAcknowledgement from '../legal-acknowledgement/pendo-legal-acknowledgement.vue.js';
import PendoModal from '../../components/modal/pendo-modal.vue.js';
import PendoMultiselect from '../../components/multiselect/pendo-multiselect.vue.js';
import PendoRepeatingTextInput from '../repeating-text-input/pendo-repeating-text-input.vue.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import omit from 'lodash/omit.js';
import cloneDeep from 'lodash/cloneDeep.js';
import RepeatingRegexpInput from './repeating-regexp-input.vue.js';

var script = {
  name: 'PendoAddUpdateExtensionAppModal',
  components: {
    PendoButton,
    PendoCheckbox,
    PendoCheckboxGroup,
    PendoModal,
    PendoLegalAcknowledgement,
    PendoMultiselect,
    PendoRepeatingTextInput,
    PendoAlert,
    PendoInput,
    RepeatingRegexpInput
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    },
    regexFlag: {
      type: Boolean,
      default: false
    },
    enableRegex: {
      type: Boolean,
      default: false
    },
    app: {
      type: Object,
      default: () => {
        return {};
      }
    },
    hasV2Permissions: {
      type: Boolean,
      default: true
    },
    isLoadingPlatformExtensionTypes: {
      type: Boolean,
      default: false
    },
    extensionPlatformTypeOptions: {
      type: Array,
      default: () => {
        return [];
      }
    },
    saving: {
      type: Boolean,
      default: false
    },
    submissionError: {
      type: Object,
      default: () => ({})
    },
    isForCustomer: {
      type: Boolean,
      default: true
    },
    width: {
      type: Number,
      default: 775
    },
    readOnlyMode: {
      type: Boolean,
      default: false
    },
    editFavicon: {
      type: Boolean,
      default: false
    },
    websitesModalAlertLink: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      appName: '',
      extensionPlatformType: {
        classExclusions: {},
        idExclusions: {}
      },
      domainPatterns: this.app.extensionDomainPatterns ? [...this.app.extensionDomainPatterns] : [''],
      regexPatterns: this.app.extensionStructRegExps ? cloneDeep(this.app.extensionStructRegExps) : [{
        subDomainRegExp: '',
        pathRegExp: '',
        rootDomain: ''
      }],
      showConfirmOptOut: false,
      showLegalAcknowledgement: false,
      pendingDataConsent: null,
      mutableSubmissionError: null,
      panels: [{
        name: 'regexp1',
        title: 'Selective URL 1'
      }],
      classValues: [],
      idValues: [],
      activePanel: ['regexp1'],
      showIds: false,
      showIdModal: false,
      shouldSetAllIds: false,
      showClassModal: false,
      showClasses: false,
      shouldSetAllClasses: false,
      pendingIdExclusions: [],
      pendingClassExclusions: []
    };
  },
  computed: {
    title() {
      if (!this.createMode) {
        if (this.readOnlyMode) {
          return this.regexFlag ? 'View Selective URLs' : 'View Websites';
        }
        if (this.regexFlag) {
          return 'Edit Selective URLs';
        }
        return this.editFavicon ? 'Edit Favicon' : 'Edit Websites';
      }
      return 'Add Extension Application';
    },
    showDomainPatterns() {
      return (this.createMode || !this.regexFlag) && !this.editFavicon;
    },
    showRegularExpressions() {
      return (this.createMode || this.regexFlag) && this.enableRegex;
    },
    showTaggingSettings() {
      const hasClassExclusions = Boolean(this.extensionPlatformType && !isEmpty(this.extensionPlatformType.classExclusions));
      const hasIdExclusions = Boolean(this.extensionPlatformType && !isEmpty(this.extensionPlatformType.idExclusions));
      const hasIdOrClassExclusions = hasClassExclusions || hasIdExclusions;
      return hasIdOrClassExclusions;
    },
    isAppNameValid() {
      return this.appName !== '';
    },
    areDomainsValid() {
      return !this.domainPatterns.some(domain => {
        return !this.isValidDomain(domain);
      });
    },
    areRegExpsValid() {
      return this.regexPatterns.every(regex => {
        return this.isEmptyRegExp(regex) || this.isRootDomainPopulated(regex) && this.isValidDomain(regex.rootDomain) && this.isValidRegExPiece(regex.subDomainRegExp, regex.rootDomain, true) && this.isValidRegExPiece(regex.pathRegExp, regex.rootDomain, false);
      });
    },
    isModalValid() {
      return this.isAppNameValid && this.areDomainsValid && this.areRegExpsValid && !!get(this.extensionPlatformType, 'type');
    },
    consentOptionsNeedingLegalAcknowledgement() {
      return Object.values(get(this.extensionPlatformType, 'consentOptions', {}) || {}).filter(consentOption => consentOption.enabled && !consentOption.legallyAcknowledged);
    },
    showDataCollectionSettings() {
      return Boolean(this.extensionPlatformType && !isEmpty(this.extensionPlatformType.consentOptions) && this.createMode);
    },
    consentOptionsAppPayload() {
      return Object.entries(get(this.extensionPlatformType, 'consentOptions', {})).reduce((appPayload, _ref) => {
        let [consentOptionKey, consentOption] = _ref;
        if (!consentOption.enabled || !consentOption.legallyAcknowledged) {
          return appPayload;
        }
        if (consentOption.subConsentOptions) {
          appPayload[consentOption.subConsentOptionsField] = Object.values(consentOption.subConsentOptions).reduce((validSubConsentOptions, subConsentOption) => {
            if (!subConsentOption.enabled) {
              return validSubConsentOptions;
            }
            switch (consentOption.subConsentOptionsPayloadFormat) {
              case 'String':
                validSubConsentOptions.push(subConsentOption.label);
                break;
              case 'Object':
                validSubConsentOptions.push(omit(subConsentOption, ['enabled', 'label']));
                break;
            }
            return validSubConsentOptions;
          }, []);
          if (consentOption.subConsentOptionsField === 'htmlAttributes') {
            return appPayload;
          }
        }
        appPayload.applicationFlags[consentOptionKey] = !appPayload.applicationFlags[consentOptionKey];
        return appPayload;
      }, {
        applicationFlags: {
          siblingSelectors: false,
          pageBasedEventProperties: false,
          disableExtensionTextCapture: true
        }
      });
    },
    populatePanels() {
      const returnPanels = [];
      for (let idx = 0; idx < this.regexPatterns.length; idx++) {
        returnPanels.push({
          name: `regexp${idx + 1}`,
          title: `Selective URL ${idx + 1}`
        });
      }
      return returnPanels;
    }
  },
  watch: {
    extensionPlatformType() {
      if (!this.extensionPlatformType) {
        return;
      }
      const classExclusions = get(this.extensionPlatformType, 'taggingClassExclusions', []).map(excl => ({
        value: true,
        label: excl,
        type: 'class',
        disabled: false
      }));
      const idExclusions = get(this.extensionPlatformType, 'taggingIdExclusions', []).map(excl => ({
        value: true,
        label: excl,
        type: 'id',
        disabled: false
      }));
      this.$set(this.extensionPlatformType, 'classExclusions', classExclusions);
      this.$set(this.extensionPlatformType, 'idExclusions', idExclusions);
      this.loadClasses(true);
      this.loadIds(true);
    },
    submissionError(value) {
      this.mutableSubmissionError = value;
    },
    visible(value) {
      if (!value) {
        this.resetModal();
      }
    },
    createMode() {
      this.regexPatterns = !this.createMode && this.app.extensionStructRegExps ? cloneDeep(this.app.extensionStructRegExps) : [{
        subDomainRegExp: '',
        pathRegExp: '',
        rootDomain: ''
      }];
      this.panels = this.populatePanels;
    }
  },
  created() {
    this.panels = this.populatePanels;
  },
  methods: {
    emitClose() {
      this.resetModal();
      this.$emit('close', true);
    },
    onClassValueChange(newVal) {
      if (this.classValues.length > newVal.length) {
        this.showClassModal = true;
        this.pendingClassExclusions = newVal;
      } else {
        this.classValues = newVal;
      }
    },
    onIdValueChange(newVal) {
      if (this.idValues.length > newVal.length) {
        this.showIdModal = true;
        this.pendingIdExclusions = newVal;
      } else {
        this.idValues = newVal;
      }
    },
    onDomainChanged() {
      const domain = this.domainPatterns[0];
      this.$emit('domain-changed', domain);
    },
    cancelClassExclusions() {
      this.showClassModal = false;
    },
    cancelIdExclusions() {
      this.showIdModal = false;
    },
    confirmClassExclusions() {
      this.classValues = this.pendingClassExclusions;
      if (!this.classValues.length) {
        this.shouldSetAllClasses = true;
      }
      this.showClassModal = false;
    },
    confirmIdExclusions() {
      this.idValues = this.pendingIdExclusions;
      if (!this.idValues.length) {
        this.shouldSetAllIds = true;
      }
      this.showIdModal = false;
    },
    loadClasses(set) {
      if (set) {
        this.classValues = this.extensionPlatformType.classExclusions.map(cls => cls.label);
        this.shouldSetAllClasses = false;
        this.showClassModal = false;
      } else {
        this.pendingClassExclusions = [];
        this.showClassModal = true;
      }
    },
    loadIds(set) {
      if (set) {
        this.idValues = this.extensionPlatformType.idExclusions.map(id => id.label);
        this.shouldSetAllIds = false;
        this.showIdModal = false;
      } else {
        this.pendingIdExclusions = [];
        this.showIdModal = true;
      }
    },
    submitModalCreateApp() {
      if (this.consentOptionsNeedingLegalAcknowledgement.length > 0) {
        this.requestConsent();
        return;
      }
      const app = _objectSpread2({
        displayName: this.appName,
        platform: 'extension',
        extensionDomainPatterns: this.domainPatterns,
        extensionStructRegExps: this.regexPatterns,
        extensionPlatformType: this.extensionPlatformType.type,
        taggingClassExclusions: this.classValues,
        taggingIdExclusions: this.idValues,
        parentAppId: this.extensionPlatformType.parentAppId
      }, this.consentOptionsAppPayload);
      this.$emit('create', app);
    },
    submitModalUpdateDomains() {
      this.$emit('update', this.domainPatterns);
    },
    submitModalUpdateRegex() {
      this.$emit('updateRegex', this.regexPatterns);
    },
    submitModalUpdateFavicon() {
      this.$emit('update-favicon');
    },
    resetError() {
      this.mutableSubmissionError = null;
      this.saving = false;
    },
    resetModal() {
      this.resetError();
      this.appName = null;
      this.extensionPlatformType = null;
      this.domainPatterns = this.app.extensionDomainPatterns ? [...this.app.extensionDomainPatterns] : [''];
      this.regexPatterns = this.app.extensionStructRegExps ? cloneDeep(this.app.extensionStructRegExps) : [{
        subDomainRegExp: '',
        pathRegExp: '',
        rootDomain: ''
      }];
      this.panels = this.regexPatterns ? [...this.panels].splice(0, this.regexPatterns.length) : [{
        name: 'regexp1',
        title: 'Regular Expression 1'
      }];
      this.activePanel = ['regexp1'];
    },
    onNameInputChange(value) {
      this.appName = value;
    },
    isValidDomain(domain) {
      if (domain === '') {
        return true;
      }
      const validDomainRegex = new RegExp('^([a-z0-9]+(-+[a-z0-9]+)*\\.)+[a-z]{2,}$', 'gm');
      const intranetHostnameRegex = new RegExp('^([a-z0-9]+(-+[a-z0-9]+)*)$');
      return validDomainRegex.test(domain) || intranetHostnameRegex.test(domain);
    },
    isValidRegExPiece(regex, rootDomain, isSubDomain) {
      if (regex === '') {
        return true;
      }
      try {
        const validRegex = new RegExp(isSubDomain ? `${regex}${rootDomain}` : `${rootDomain}${regex}`);
        return true;
      } catch (err) {
        return false;
      }
    },
    isEmptyRegExp(regex) {
      return regex.rootDomain === '' && regex.pathRegExp === '' && regex.subDomainRegExp === '';
    },
    isRootDomainPopulated(regex) {
      return regex.rootDomain !== '' && (regex.pathRegExp !== '' || regex.subDomainRegExp !== '');
    },
    toggleConsent(consentOptionKey, value, subConsentOptionKey) {
      this.pendingDataConsent = {
        consentOptionKey,
        subConsentOptionKey
      };
      if (value === false) {
        this.showConfirmOptOut = true;
        return;
      }
      this.cancelOptOut();
    },
    toggleSubConsentOptions(consentOption) {
      const currentOption = get(this.extensionPlatformType.consentOptions[consentOption], 'isSubConsentOptionsExpanded', false);
      Vue.set(this.extensionPlatformType.consentOptions[consentOption], 'isSubConsentOptionsExpanded', !currentOption);
    },
    changeConsentOption(enabled) {
      const {
        consentOptionKey,
        subConsentOptionKey
      } = this.pendingDataConsent;
      if (subConsentOptionKey) {
        Vue.set(this.extensionPlatformType.consentOptions[consentOptionKey].subConsentOptions[subConsentOptionKey], 'enabled', enabled);
        const someSubConsentOptionsAreEnabled = Object.values(this.extensionPlatformType.consentOptions[consentOptionKey].subConsentOptions).some(subConsentOption => subConsentOption.enabled);
        Vue.set(this.extensionPlatformType.consentOptions[consentOptionKey], 'enabled', someSubConsentOptionsAreEnabled);
        return;
      }
      Vue.set(this.extensionPlatformType.consentOptions[consentOptionKey], 'enabled', enabled);
      for (const subConsentOptionKey in this.extensionPlatformType.consentOptions[consentOptionKey].subConsentOptions) {
        Vue.set(this.extensionPlatformType.consentOptions[consentOptionKey].subConsentOptions[subConsentOptionKey], 'enabled', enabled);
      }
    },
    confirmOptOut() {
      this.changeConsentOption(false);
      this.resetOptOut();
    },
    cancelOptOut() {
      this.changeConsentOption(true);
      this.resetOptOut();
    },
    resetOptOut() {
      this.pendingDataConsent = null;
      this.showConfirmOptOut = false;
    },
    requestConsent() {
      this.showLegalAcknowledgement = true;
    },
    confirmConsent() {
      for (const consentOptionKey in this.extensionPlatformType.consentOptions) {
        Vue.set(this.extensionPlatformType.consentOptions[consentOptionKey], 'legallyAcknowledged', true);
      }
      this.showLegalAcknowledgement = false;
      this.submitModalCreateApp();
    },
    cancelConsent() {
      this.showLegalAcknowledgement = false;
    },
    updatePanel(value) {
      this.panels = value;
    },
    updateActivePanel(value) {
      this.activePanel = value;
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-add-update-extension-app-modal.vue2.js.map
